import { keyBy } from "es-toolkit";
import { produce } from "immer";
import { useAtomValue, useSetAtom } from "jotai";
import { loadable } from "jotai/utils";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";

import type { DocumentMetadata, PageMetadata } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";

import { DAILY_NOTES_DOCUMENT_TYPE } from "../../internal/daily-notes";
import { PRIMARY_AGENTS_MD_PAGE_ID } from "../../internal/document";
import { $documentLocalPath } from "../../internal/document-list";
import { metadataKeyForDocsRoute } from "../../internal/document-metadata/documentMetadataKey";
import {
	$documentMetadata,
	$documentMetadataError,
} from "../../internal/document-metadata/metadata-state.atom";
import { env } from "../../internal/env";
import { exists, rename } from "../../internal/filesystem";
import { $updatePageLinks } from "../../internal/page-link";
import {
	buildFullSlugPath,
	generateUniqueSlug,
	sluggify,
} from "../../internal/page-metadata/page-metadata.utils";
import {
	generateUniquePageTitle,
	isPageDisplayTitleUnique,
} from "../../internal/page-metadata/page-title.utils";
import {
	$beginRenamingPage,
	$endRenamingPage,
	$renamePageDir,
} from "../../internal/page/rename-page-dir.atom";
import { getPageDirPath } from "../../internal/paths";
import { $dropRequestPageHistory, $renameRequestPageHistory } from "../../internal/request-page";
import {
	getCurrentReorderPagePath,
	getTemporaryReorderPagePath,
	getTitleBasedPageSlug,
	isPageIdUnique as isPageIdUniqueForPages,
	resolveConflictingIndexSlugs,
} from "./useDocumentMetadata.utils";
import { usePageFromCurrentRoute } from "./usePageFromCurrentRoute";
import { useRouteParams } from "./useRouteParams";

const logger = new Logger("useDocumentMetadata");

export const useDocumentMetadata = (documentId: string) => {
	const router = useRouter();
	const { workspaceName } = useRouteParams();
	const { pageId: currentPageId } = usePageFromCurrentRoute();
	const metadataKey = useMemo(() => metadataKeyForDocsRoute(documentId), [documentId]);

	const metadata = useAtomValue($documentMetadata(metadataKey));
	const updateMetadata = useSetAtom($documentMetadata(metadataKey));
	const error = useAtomValue($documentMetadataError(metadataKey));
	const dropRequestPageHistory = useSetAtom($dropRequestPageHistory);
	const renameRequestPageHistory = useSetAtom($renameRequestPageHistory);
	const documentLocalPathLoadable = useAtomValue(loadable($documentLocalPath(documentId)));
	const documentLocalPath =
		documentLocalPathLoadable.state === "hasData" ? documentLocalPathLoadable.data : undefined;
	const renamePageDirAction = useSetAtom($renamePageDir);
	const beginRenamingPage = useSetAtom($beginRenamingPage);
	const endRenamingPage = useSetAtom($endRenamingPage);
	const updatePageLinks = useSetAtom($updatePageLinks({ documentId }));

	const syncCurrentPageRoute = useCallback(
		async (pages: PageMetadata[], pageId: string) => {
			if (!documentId || !workspaceName || currentPageId !== pageId) {
				return;
			}

			const renamedPage = pages.find((page) => page.id === pageId);
			if (!renamedPage) {
				return;
			}

			const pageIdentifier = env.isDesktopApp
				? renamedPage.id
				: buildFullSlugPath(pages, renamedPage);
			const nextPath =
				pageIdentifier && pageIdentifier !== "index"
					? `/d/${workspaceName}/${documentId}/${pageIdentifier}`
					: `/d/${workspaceName}/${documentId}`;
			const queryString = router.asPath.includes("?")
				? router.asPath.slice(router.asPath.indexOf("?"))
				: "";

			if (router.asPath === `${nextPath}${queryString}`) {
				return;
			}

			await router.replace(`${nextPath}${queryString}`);
		},
		[currentPageId, documentId, router, workspaceName]
	);

	const reorderPages = useCallback(
		async (reorderInfo: { pageId: string; parentPageID: string | null }[]) => {
			if (!metadata?.pagesMetadata) {
				return;
			}

			const isDailyNotes = metadata.type === DAILY_NOTES_DOCUMENT_TYPE;
			const originalPagesById = keyBy(metadata.pagesMetadata, (page) => page.id);
			const originalIndexPageId = metadata.pagesMetadata.find(
				(page) => page.slug === "index"
			)?.id;

			let sortedPages = reorderInfo.reduce<DocumentMetadata["pagesMetadata"]>(
				(acc, { pageId, parentPageID }, index) => {
					const page = originalPagesById[pageId];
					if (page) {
						const newPage = { ...page, parentPageID };
						if (!isDailyNotes) {
							if (index === 0) {
								newPage.slug = "index";
								if (page.slug !== "index") {
									newPage.path = getCurrentReorderPagePath(
										metadata.pagesMetadata,
										page
									);
								}
							}
						}
						acc.push(newPage);
					}
					return acc;
				},
				[]
			);

			if (!isDailyNotes && originalIndexPageId) {
				const promotedPageId = reorderInfo[0]?.pageId;
				const formerIndexPage = sortedPages.find((page) => page.id === originalIndexPageId);

				if (formerIndexPage && formerIndexPage.id !== promotedPageId) {
					formerIndexPage.slug = getTitleBasedPageSlug(formerIndexPage, sortedPages);
				}
			}

			if (!isDailyNotes) {
				sortedPages = resolveConflictingIndexSlugs(sortedPages);
			}

			if (!isDailyNotes) {
				const pagesWithChangedSlugs = sortedPages.filter((sortedPage) => {
					const originalPage = metadata.pagesMetadata.find((p) => p.id === sortedPage.id);
					return originalPage && originalPage.slug !== sortedPage.slug;
				});

				if (pagesWithChangedSlugs.length > 0) {
					await beginRenamingPage(documentId);
					try {
						for (const sortedPage of pagesWithChangedSlugs) {
							const originalPage = originalPagesById[sortedPage.id];
							if (!originalPage) {
								continue;
							}

							const currentPath = getCurrentReorderPagePath(
								metadata.pagesMetadata,
								originalPage
							);
							const temporaryPath = await renamePageDirAction({
								documentId,
								pageId: sortedPage.id,
								newSlug: sortedPage.slug,
								currentPath,
								targetDirRelative: getTemporaryReorderPagePath(
									sortedPage.id,
									currentPath,
									sortedPage.type
								).replace(/\/[^/]+$/, ""),
							});
							if (temporaryPath) {
								sortedPage.path = temporaryPath;
							}
						}

						for (const sortedPage of pagesWithChangedSlugs) {
							const originalPage = originalPagesById[sortedPage.id];
							if (!originalPage) {
								continue;
							}

							const currentPath =
								sortedPage.path ??
								getCurrentReorderPagePath(metadata.pagesMetadata, originalPage);
							const targetPathSlug =
								sortedPage.slug === "index"
									? getTitleBasedPageSlug(sortedPage, sortedPages)
									: sortedPage.slug;
							const newPath = await renamePageDirAction({
								documentId,
								pageId: sortedPage.id,
								newSlug: sortedPage.slug,
								currentPath,
								targetDirRelative: `pages/${targetPathSlug}`,
							});
							if (newPath) {
								sortedPage.path = newPath;
							}
						}
					} finally {
						await endRenamingPage(documentId);
					}
				}
			}

			await updatePageLinks({
				pagesBefore: metadata.pagesMetadata,
				pagesAfter: sortedPages,
			});

			await updateMetadata({ pagesMetadata: sortedPages });
		},
		[
			metadata,
			updateMetadata,
			renamePageDirAction,
			documentId,
			updatePageLinks,
			beginRenamingPage,
			endRenamingPage,
		]
	);

	const renamePage = useCallback(
		async (pageId: string, newName: string) => {
			if (!metadata?.pagesMetadata) {
				return;
			}

			const pageBeingRenamed = metadata.pagesMetadata.find((p) => p.id === pageId);
			if (!pageBeingRenamed || pageBeingRenamed.title === newName) {
				return;
			}

			const isDailyNotes = metadata.type === DAILY_NOTES_DOCUMENT_TYPE;
			let newSlug = "";
			let renamedPathSlug = "";
			const nextTitle = generateUniquePageTitle(newName, pageId, metadata.pagesMetadata);
			const titleSlug = sluggify(nextTitle);

			const updatedPagesMetadata = produce(metadata.pagesMetadata, (draft) => {
				const page = draft.find((p) => p.id === pageId);
				if (page) {
					page.title = nextTitle;

					if (!isDailyNotes && nextTitle.trim() !== "") {
						if (page.slug === "index") {
							if (titleSlug !== "") {
								renamedPathSlug = getTitleBasedPageSlug(page, draft);
							}
						} else {
							newSlug = titleSlug;
							if (newSlug !== "") {
								page.slug = generateUniqueSlug(
									newSlug,
									pageId,
									page.parentPageID,
									draft
								);
								renamedPathSlug = page.slug;
							}
						}
					}
				}
			});

			if (renamedPathSlug !== "") {
				const updatedPage = updatedPagesMetadata.find((p) => p.id === pageId);
				const actualSlug = updatedPage?.slug ?? pageBeingRenamed.slug;
				const shouldUseExplicitPathTarget = renamedPathSlug !== actualSlug;
				const newPath = await renamePageDirAction({
					documentId,
					pageId,
					newSlug: actualSlug,
					currentPath: getCurrentReorderPagePath(
						metadata.pagesMetadata,
						pageBeingRenamed
					),
					...(shouldUseExplicitPathTarget && {
						targetDirRelative: `pages/${renamedPathSlug}`,
					}),
				});

				if (newPath) {
					const pagesWithPath = produce(updatedPagesMetadata, (draft) => {
						const page = draft.find((p) => p.id === pageId);
						if (page) {
							page.path = newPath;
						}
					});

					await updatePageLinks({
						pagesBefore: metadata.pagesMetadata,
						pagesAfter: pagesWithPath,
					});

					const shouldUpdateDocumentTitle =
						metadata.pagesMetadata.length === 1 && metadata.title === "";

					await updateMetadata({
						...(shouldUpdateDocumentTitle && { title: nextTitle }),
						pagesMetadata: pagesWithPath,
					});
					await syncCurrentPageRoute(pagesWithPath, pageId);
					return;
				}
			}

			await updatePageLinks({
				pagesBefore: metadata.pagesMetadata,
				pagesAfter: updatedPagesMetadata,
			});

			const shouldUpdateDocumentTitle =
				metadata.pagesMetadata.length === 1 && metadata.title === "";

			await updateMetadata({
				...(shouldUpdateDocumentTitle && { title: nextTitle }),
				pagesMetadata: updatedPagesMetadata,
			});
			await syncCurrentPageRoute(updatedPagesMetadata, pageId);
		},
		[
			metadata,
			updateMetadata,
			renamePageDirAction,
			documentId,
			updatePageLinks,
			syncCurrentPageRoute,
		]
	);

	const updatePageMetadata = useCallback(
		async (pageId: string, updates: Partial<PageMetadata>) => {
			if (!metadata?.pagesMetadata) {
				return;
			}

			// If the page ID is changing, rename the page directory on disk first
			if (updates.id && updates.id !== pageId && documentLocalPath) {
				try {
					const oldPageDir = await getPageDirPath(documentLocalPath, pageId);
					const newPageDir = await getPageDirPath(documentLocalPath, updates.id);
					const oldExists = await exists(oldPageDir);
					if (oldExists) {
						await rename(oldPageDir, newPageDir);
					}
				} catch (error) {
					logger.error("Failed to rename page directory", {
						pageId,
						newId: updates.id,
						error,
					});
					throw error;
				}
			}

			const normalizedUpdates =
				updates.title !== undefined
					? {
							...updates,
							title: generateUniquePageTitle(
								updates.title,
								pageId,
								metadata.pagesMetadata
							),
						}
					: updates;
			const updatedPagesMetadata = produce(metadata.pagesMetadata, (draft) => {
				const pageIndex = draft.findIndex((p) => p.id === pageId);
				if (pageIndex !== -1) {
					if (normalizedUpdates.id && normalizedUpdates.id !== pageId) {
						draft[pageIndex] = { ...draft[pageIndex], ...normalizedUpdates };

						for (const page of draft) {
							if (page.parentPageID === pageId) {
								page.parentPageID = normalizedUpdates.id;
							}
						}
					} else {
						Object.assign(draft[pageIndex], normalizedUpdates);
					}
				}
			});

			await updatePageLinks({
				pagesBefore: metadata.pagesMetadata,
				pagesAfter: updatedPagesMetadata,
			});

			const shouldUpdateDocumentTitle =
				metadata.pagesMetadata.length === 1 &&
				metadata.title === "" &&
				normalizedUpdates.title !== undefined;

			await updateMetadata({
				...(shouldUpdateDocumentTitle && { title: normalizedUpdates.title }),
				pagesMetadata: updatedPagesMetadata,
			});

			const existingPage = metadata.pagesMetadata.find((page) => page.id === pageId);
			if (existingPage?.type === "request" && updates.id && updates.id !== pageId) {
				await renameRequestPageHistory({
					documentId,
					fromPageId: pageId,
					toPageId: updates.id,
				});
			}
		},
		[
			documentId,
			documentLocalPath,
			metadata,
			renameRequestPageHistory,
			updateMetadata,
			updatePageLinks,
		]
	);

	const deletePage = useCallback(
		async (pageId: string) => {
			if (!metadata?.pagesMetadata) {
				return;
			}

			const nonArchivedPages = metadata.pagesMetadata.filter((p) => !p.archived);
			if (nonArchivedPages.length <= 1) {
				return;
			}

			const updatedPagesMetadata = metadata.pagesMetadata.filter((p) => p.id !== pageId);

			const pagesWithoutOrphans = updatedPagesMetadata.map((page) => {
				if (page.parentPageID === pageId) {
					return { ...page, parentPageID: null };
				}
				return page;
			});

			await updatePageLinks({
				pagesBefore: metadata.pagesMetadata,
				pagesAfter: pagesWithoutOrphans,
			});

			await updateMetadata({ pagesMetadata: pagesWithoutOrphans });

			const deletedPage = metadata.pagesMetadata.find((page) => page.id === pageId);
			if (deletedPage?.type === "request") {
				await dropRequestPageHistory({ documentId, pageId });
			}
		},
		[documentId, dropRequestPageHistory, metadata, updateMetadata, updatePageLinks]
	);

	const isPageIdUnique = useCallback(
		(id: string, currentPageId: string) => {
			if (!metadata?.pagesMetadata) {
				return true;
			}

			return isPageIdUniqueForPages(id, currentPageId, metadata.pagesMetadata, [
				PRIMARY_AGENTS_MD_PAGE_ID,
			]);
		},
		[metadata]
	);

	const isSlugUnique = useCallback(
		(slug: string, pageId: string, parentPageID: string | null) => {
			if (!metadata?.pagesMetadata) {
				return true;
			}

			const siblingPages = metadata.pagesMetadata.filter((p) => {
				if (p.id === pageId) {
					return false;
				}

				if (parentPageID == null) {
					return p.parentPageID == null;
				}

				return p.parentPageID === parentPageID;
			});

			return !siblingPages.some((p) => p.slug === slug);
		},
		[metadata]
	);

	const isPageTitleUnique = useCallback(
		(title: string, pageId?: string) => {
			if (!metadata?.pagesMetadata) {
				return true;
			}

			return isPageDisplayTitleUnique(title, pageId, metadata.pagesMetadata);
		},
		[metadata]
	);

	return {
		metadataKey,
		metadata,
		updateMetadata,
		reorderPages,
		renamePage,
		updatePageMetadata,
		deletePage,
		isPageIdUnique,
		isPageTitleUnique,
		isSlugUnique,
		error,
		isLoading: !metadata,
		isError: !!error,
	};
};

export const useDocumentPageMetadataById = (documentId: string) => {
	const { metadata } = useDocumentMetadata(documentId);

	return useMemo<Record<string, PageMetadata>>(() => {
		if (!metadata) {
			return {};
		}

		return keyBy(metadata.pagesMetadata, (page) => page.id);
	}, [metadata]);
};
